import { getAuth } from '@/config/firebase';

interface TokenMonitorConfig {
  onTokenExpired?: () => void;
  onTokenRefreshed?: (token: string) => void;
  onTokenError?: (error: any) => void;
}

class TokenMonitorService {
  private intervalId: NodeJS.Timer | null = null;
  private config: TokenMonitorConfig = {};
  private lastTokenExpirationTime: number | null = null;
  
  // Check token every 5 minutes
  private readonly CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
  
  // Refresh token if it expires within 10 minutes
  private readonly REFRESH_THRESHOLD = 10 * 60 * 1000; // 10 minutes
  
  start(config?: TokenMonitorConfig) {
    if (this.intervalId) {
      console.warn('[TokenMonitor] Already running');
      return;
    }
    
    this.config = config || {};
    console.log('[TokenMonitor] Starting token monitor');
    
    // Check immediately
    this.checkToken();
    
    // Then check periodically
    this.intervalId = setInterval(() => {
      this.checkToken();
    }, this.CHECK_INTERVAL);
  }
  
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('[TokenMonitor] Stopped');
    }
  }
  
  private async checkToken() {
    try {
      const auth = await getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.log('[TokenMonitor] No user logged in');
        return;
      }
      
      // Get token result without forcing refresh
      const tokenResult = await user.getIdTokenResult(false);
      
      // Check expiration time
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      const timeUntilExpiry = expirationTime - now;
      
      console.log('[TokenMonitor] Token status:', {
        expiresIn: Math.round(timeUntilExpiry / 1000 / 60) + ' minutes',
        hasCustomClaims: !!(tokenResult.claims.role && tokenResult.claims.tenantId)
      });
      
      // Store expiration time to detect changes
      if (this.lastTokenExpirationTime && expirationTime !== this.lastTokenExpirationTime) {
        console.log('[TokenMonitor] Token was refreshed');
        this.config.onTokenRefreshed?.(tokenResult.token);
      }
      this.lastTokenExpirationTime = expirationTime;
      
      // If token expires soon, refresh it proactively
      if (timeUntilExpiry < this.REFRESH_THRESHOLD) {
        console.log('[TokenMonitor] Token expiring soon, refreshing...');
        
        try {
          const newTokenResult = await user.getIdTokenResult(true);
          console.log('[TokenMonitor] Token refreshed successfully');
          this.config.onTokenRefreshed?.(newTokenResult.token);
          this.lastTokenExpirationTime = new Date(newTokenResult.expirationTime).getTime();
        } catch (error) {
          console.error('[TokenMonitor] Failed to refresh token:', error);
          this.config.onTokenError?.(error);
        }
      }
      
      // Check if token is already expired
      if (timeUntilExpiry <= 0) {
        console.error('[TokenMonitor] Token is expired!');
        this.config.onTokenExpired?.();
      }
      
    } catch (error) {
      console.error('[TokenMonitor] Error checking token:', error);
      this.config.onTokenError?.(error);
    }
  }
  
  // Force an immediate token check
  async forceCheck() {
    console.log('[TokenMonitor] Forcing token check');
    await this.checkToken();
  }
}

// Export singleton instance
export const tokenMonitor = new TokenMonitorService();

// Auto-start monitoring when user is logged in
export function startTokenMonitoring(config?: TokenMonitorConfig) {
  tokenMonitor.start(config);
}

export function stopTokenMonitoring() {
  tokenMonitor.stop();
}